/** English strings for the compliance area. The Vietnamese file must have exactly the same keys. */
export const compliance = {
  crumbs: { compliance: 'Compliance', icann: 'ICANN', vnnic: 'VNNIC' },
  common: {
    needsPermission: 'Needs',
    andMfa: 'and an MFA-verified session.',
    needsMfa: 'Needs an MFA-verified session.',
    approved: 'Approved',
    notApproved: 'Not approved',
    any: 'Any',
    all: 'All',
    required: 'required',
    optional: 'optional',
    notManaged: 'Not a managed domain',
    correlation: 'Correlation',
    none: 'None',
    details: 'Details',
    hide: 'Hide',
    showingFirst: 'Showing the first {{count}}',
    rows: '{{count}} shown',
    exactName: 'exact name',
    by: 'by',
    domain: 'Domain',
    status: 'Status',
    reason: 'Reason',
    note: 'Note',
    auditedReason: 'Reason (required, recorded in the audit log)',
    idempotent: 'Retrying after a failure is safe: the same change is never applied twice.',
    saving: 'Saving…',
    tabs: {
      summary: 'Summary',
      evidence: 'Evidence',
      actions: 'Actions',
      history: 'History',
    },
    overdueCount: '{{count}} overdue',
    notFoundHint: 'Check the link, or go back to the list.',
  },
  sla: {
    overdue: 'Overdue',
    dueSoon: 'Due soon',
    noPolicy: 'No SLA policy',
    noPolicyHint: 'No deadline policy was in force when the case was opened',
    responded: 'Responded {{when}}',
  },
  abuse: {
    list: {
      title: 'Abuse cases',
      subtitle: 'Reports of phishing, malware, spam and other abuse. Respond before the first-response deadline; overdue cases are listed first.',
      register: 'Register report',
      loadError: 'Could not load abuse cases',
      empty: 'No cases match these filters',
      emptyHint: 'Change or reset the filters to see more cases.',
      overdueOnly: 'Overdue only',
      stats: {
        open: 'Open cases',
        overdue: 'Overdue first response',
        actionRequired: 'Action required',
        critical: 'Critical priority',
        overdueHint: 'Respond to these first.',
        allOnTime: 'All within deadline',
      },
      col: {
        case: 'Case',
        domain: 'Domain',
        category: 'Category',
        priority: 'Priority',
        status: 'Status',
        firstResponse: 'First response',
        reports: 'Reports',
        opened: 'Opened',
      },
      filter: { status: 'Status', category: 'Category', domain: 'Domain' },
    },
    form: {
      title: 'Register an abuse report',
      description: 'A report for a name and category that already has an open case is attached to that case instead of opening a new one.',
      domain: 'Domain (required)',
      category: 'Category',
      priority: 'Priority',
      source: 'Source',
      channel: 'Channel',
      reporterType: 'Reporter type',
      reporterContact: 'Reporter contact (optional, stored encrypted)',
      externalRef: 'External reference (optional)',
      summary: 'Summary (required)',
      submit: 'Register report',
      busy: 'Registering…',
    },
    detail: {
      title: 'Abuse case',
      loadError: 'Could not load the case',
      attached: 'The report was attached to this existing open case (same name and category).',
      overdue: 'The first response is overdue since {{when}}. Respond to the reporter now.',
      facts: 'Case details',
      source: 'Source',
      opened: 'Opened',
      firstResponse: 'First response',
      respondedAt: 'Responded {{at}}',
      dueAt: 'Due {{at}} ({{rel}})',
      noPolicy: 'No SLA policy was in force',
      slaPolicy: 'SLA policy',
      assignedTo: 'Assigned to',
      statusChanged: 'Status changed',
      resolution: 'Resolution',
      version: 'Version',
      reports: 'Reports ({{count}})',
      noReports: 'No reports',
      contactOnFile: 'contact on file (encrypted)',
      ref: 'ref {{ref}}',
      evidence: 'Evidence ({{count}})',
      evidenceHint: 'Append-only: evidence is never edited or deleted. The SHA-256 proves what was captured.',
      noEvidence: 'No evidence yet',
      addEvidence: 'Add evidence',
      col: {
        captured: 'Captured',
        description: 'Description',
        source: 'Source',
        sha: 'SHA-256',
        requested: 'Requested',
        action: 'Action',
        status: 'Status',
        reason: 'Reason',
        registry: 'Registry',
      },
      actions: 'Domain actions ({{count}})',
      actionsHint: 'Suspensions and locks applied to the domain because of this case.',
      noActions: 'No domain actions',
      requestAction: 'Request domain action',
      reverses: 'reverses: {{action}}',
      noRegistryCommand: 'no registry command',
      result: 'result {{code}}',
      le: 'Law-enforcement requests ({{count}})',
      leHint: 'Requests from authorities (police, courts, regulators) linked to this case.',
      noLe: 'No requests from authorities',
      recordLe: 'Record request',
      leMeta: '{{jurisdiction}} · {{basis}} · received {{at}}',
      leDeadline: 'deadline {{at}} ({{rel}})',
      leResponse: 'Response: {{text}}',
      changeStatus: 'Change status',
      statusHint: 'Move the case through triage, investigation and resolution.',
      noTransitions: 'This case is closed; its status can no longer change.',
      history: 'Case log',
      noHistory: 'No entries yet',
      transition: '{{from}} → {{to}}',
      noDomain: 'Domain actions need a managed domain; this name is not managed here.',
      closedHint: 'The case is closed: evidence, domain actions and requests can no longer be added.',
    },
    transition: {
      newStatus: 'New status',
      note: 'Note (optional, audited)',
      resolution: 'Resolution',
      resolutionRequired: 'Resolution (required)',
      resolutionReplace: 'Resolution (already set; editing replaces it)',
      version: 'Sent with version {{version}}; if someone changed the case meanwhile, reload and retry.',
      submit: 'Change status',
    },
    evidence: {
      title: 'Add evidence',
      description: 'Evidence is append-only and recorded with your identity.',
      url: 'URL (http/https)',
      or: 'or',
      documentId: 'Document ID (a CASE document)',
      captured: 'Captured at (UTC, optional)',
      sha: 'SHA-256 of the captured content (64 hex)',
      shaInvalid: 'Must be 64 hexadecimal characters.',
      description_: 'Description (required)',
      submit: 'Add evidence',
      busy: 'Adding…',
    },
    action: {
      title: 'Domain action on {{domain}}',
      description: 'The action is applied to the domain and recorded on the case.',
      action: 'Action',
      reason: 'Reason (required, audited)',
      confirm: 'I understand this changes {{domain}} at the registry (EPP update).',
      confirmLocal: 'I confirm this action for {{domain}}.',
      idempotent:
        'Retrying after a failure is safe: the action is never carried out twice, and a command whose outcome at the registry is still unknown is never sent again.',
      submit: 'Request: {{action}}',
      busy: 'Requesting…',
      help: {
        SUSPEND: 'Registry clientHold + service SUSPENDED + compliance lock. Sends an EPP update.',
        UNSUSPEND: 'Removes clientHold and releases the suspension lock. Sends an EPP update.',
        LOCK: 'Local compliance lock only; no registry change.',
        UNLOCK: 'Releases a lock applied by a LOCK action of this case.',
        NOTE_ONLY: 'Records a decision to take no domain action.',
      },
    },
    leForm: {
      title: 'Record a law-enforcement request',
      description: 'A request from an authority about this case. Its deadline is tracked with the case.',
      agency: 'Agency',
      jurisdiction: 'Jurisdiction (ISO 3166, e.g. VN, US-CA)',
      reference: 'Reference',
      deadline: 'Response deadline (UTC, optional)',
      legalBasis: 'Legal basis',
      submit: 'Record request',
      busy: 'Recording…',
      newStatus: 'New status',
      responseSummary: 'Response summary',
      update: 'Update',
    },
  },
  disputes: {
    list: {
      title: 'Disputes',
      subtitle: 'UDRP / URS proceedings, court orders and other disputes. Each one holds a legal lock on the domain until it is closed with four-eyes approval.',
      open: 'Open dispute',
      loadError: 'Could not load disputes',
      empty: 'No disputes match',
      col: {
        domain: 'Domain',
        type: 'Type / provider',
        reference: 'Reference',
        status: 'Status',
        lock: 'Legal lock',
        opened: 'Opened',
      },
      lockActive: 'active',
      lockReleased: 'released',
      stats: {
        open: 'Open disputes',
        locked: 'Domains under legal lock',
        decided: 'Decided, awaiting implementation',
      },
    },
    form: {
      title: 'Open a dispute',
      description: 'Opening a dispute immediately applies a legal lock to the domain (UDRP/URS lock, court lock, registry or compliance lock).',
      domain: 'Domain (name or ID)',
      type: 'Type',
      provider: 'Provider / court',
      reference: 'Case reference',
      abuseCase: 'Linked abuse case ID (optional)',
      summary: 'Summary (optional)',
      confirm: 'I confirm the dispute is genuine and the domain must be locked now.',
      submit: 'Open dispute and lock',
      busy: 'Opening…',
    },
    detail: {
      title: 'Dispute',
      loadError: 'Could not load the dispute',
      facts: 'Dispute details',
      opened: 'Opened',
      decision: 'Decision',
      decisionSummary: 'Decision summary',
      implemented: 'Implemented',
      closed: 'Closed',
      closeReason: 'Close reason',
      releaseApproval: 'Release approval',
      abuseCase: 'Linked abuse case',
      summary: 'Summary',
      lock: 'Legal lock',
      lockLabel: 'Lock',
      applied: 'Applied',
      reason: 'Reason',
      released: 'Released',
      noLock: 'No lock recorded.',
      lockId: 'Lock {{id}}',
      finished: 'This dispute is finished; no further action applies.',
      steps: 'Next steps',
      decide: 'Record decision',
      decideHint: 'Available once the domain is locked.',
      implement: 'Mark implemented',
      implementHint: 'Available after a decision is recorded.',
      withdraw: 'Withdraw and release lock…',
      close: 'Close and release lock…',
      closeHint: 'Releasing the legal lock needs a four-eyes approval by a second operator.',
      history: {
        opened: 'Dispute opened',
        lockApplied: 'Legal lock applied',
        decided: 'Decision recorded: {{decision}}',
        implemented: 'Decision implemented',
        closed: 'Dispute closed',
        lockReleased: 'Legal lock released',
      },
    },
    decide: {
      title: 'Record the decision',
      description: 'Record the provider’s or court’s decision exactly as issued. The decision is audited and drives the implementation.',
      decision: 'Decision',
      summary: 'Decision summary (optional)',
      confirm: 'I confirm this matches the official decision document.',
      submit: 'Record decision',
    },
    implement: {
      title: 'Mark the decision implemented',
      note: 'Note (required): what was done, e.g. transfer completed',
      submit: 'Mark implemented',
    },
    close: {
      titleWithdraw: 'Withdraw the dispute and release the legal lock',
      titleClose: 'Close the dispute and release the legal lock',
      description: 'Four-eyes: you request an approval, a different operator decides it, then the dispute is closed with the approved request ID.',
      step1: 'Request a four-eyes approval',
      step1Body: 'Action to approve on this dispute:',
      requestReason: 'Why the lock can be released',
      requestSubmit: 'Request approval',
      requestBusy: 'Requesting…',
      requested: 'Approval requested:',
      requestedHint: 'A different operator must approve it.',
      step2: 'A second operator decides',
      step2Body: 'The requester cannot approve their own request.',
      noPending: 'No pending approval for this dispute.',
      requestedBy: 'requested by',
      expires: 'expires {{when}}',
      decisionReason: 'Decision reason (required)',
      approve: 'Approve',
      reject: 'Reject',
      step3: 'Close with the approved request ID',
      outcome: 'Outcome',
      approvalId: 'Approved request ID (required)',
      reason: 'Reason (required, recorded on the lock release)',
      confirm: 'I understand this releases the legal lock on {{domain}}.',
      submitWithdraw: 'Withdraw and release lock',
      submitClose: 'Close and release lock',
      busy: 'Closing…',
    },
  },
  disclosure: {
    list: {
      title: 'Disclosure requests',
      subtitle: 'Requests to disclose non-public registration data (authorities, rights holders, security researchers). Answer by the deadline; overdue first.',
      register: 'Register request',
      loadError: 'Could not load disclosure cases',
      empty: 'No disclosure cases match',
      col: {
        case: 'Case',
        domain: 'Domain',
        requester: 'Requester',
        status: 'Status',
        fields: 'Fields',
        deadline: 'Deadline',
      },
      noDeadline: 'No policy deadline',
      stats: {
        open: 'Open requests',
        overdue: 'Overdue',
        toRelease: 'Approved, not yet released',
      },
    },
    form: {
      title: 'Register a disclosure request',
      description: 'The deadline is set from the disclosure policy in force. Personal data is disclosed only after an independent decision.',
      domain: 'Domain',
      requesterType: 'Requester type',
      requesterName: 'Requester name',
      requesterEmail: 'Requester email',
      organization: 'Organization (optional)',
      legalBasis: 'Legal basis',
      purpose: 'Purpose',
      fields: 'Requested fields',
      urgent: 'Urgent: imminent threat to life, serious injury, critical infrastructure or child exploitation (shorter deadline)',
      submit: 'Register request',
      busy: 'Registering…',
    },
    detail: {
      title: 'Disclosure request',
      loadError: 'Could not load the case',
      noPolicy: 'No disclosure policy was in force when this was registered, so it has no response deadline. Track it manually.',
      overdue: 'The response deadline passed {{when}}.',
      requester: 'Requester',
      requesterEmail: 'Requester email',
      legalBasis: 'Legal basis',
      purpose: 'Purpose',
      received: 'Received',
      deadline: 'Deadline',
      policy: 'Policy',
      registeredBy: 'Registered by',
      requestedFields: 'Requested fields',
      decision: 'Decision',
      reviewer: 'Reviewer',
      documents: 'Supporting documents',
      request: 'Request',
      requesterSection: 'Requester',
      decisionSection: 'Decision',
      evidenceHint: 'Supporting documents submitted with the request.',
      noDocuments: 'No supporting documents',
      startReview: 'Start review',
      resumeReview: 'Resume review',
      decide: 'Decide…',
      requestInfo: 'Request more information…',
      release: 'Release data…',
      close: 'Close case…',
      nextStep: 'What you can do now',
      noAction: 'No action applies in this status.',
      releaseLog: 'Release log',
      nothingReleased: 'Nothing released',
      releaseLogNote: 'The released values are never stored; the digest proves what was sent.',
      col: {
        released: 'Released',
        fields: 'Fields',
        channel: 'Channel',
        by: 'By',
        sha: 'Payload SHA-256',
      },
      history: {
        received: 'Request received',
        decided: 'Decision recorded',
        released: 'Data released via {{channel}}',
      },
    },
    info: {
      title: 'Request more information',
      label: 'What is missing (sent to the requester, audited)',
      submit: 'Request information',
    },
    closeForm: {
      title: 'Close the case',
      label: 'Reason (required)',
      submit: 'Close case',
    },
    decide: {
      title: 'Decide the request',
      description: 'Approve only what the legal basis justifies; nothing approved means the request is denied. The decision is final.',
      fourEyes: 'Four-eyes: the decider must be a different person from whoever registered the request.',
      fields: 'Fields to disclose',
      reason: 'Reason (required, recorded with the decision)',
      confirm: 'I have checked the legal basis and purpose, and this decision is final.',
      deny: 'Deny',
      approveAll: 'Approve all',
      approvePartial: 'Approve partially',
    },
    release: {
      title: 'Release the approved data',
      description: 'Returns {{count}} approved field(s) exactly once for delivery through the channel below. Only a SHA-256 digest is stored.',
      channel: 'Delivery channel (required), e.g. encrypted email to the requester, secure portal upload',
      confirm: 'I will deliver the data only through this channel and will not copy it anywhere else.',
      submit: 'Release once',
      busy: 'Releasing…',
    },
    released: {
      title: 'Personal data: shown once',
      body: 'Deliver it through the recorded channel now. It is not stored anywhere in this console and cannot be shown again; a second release would be a new, separately audited disclosure. Do not paste it into tickets or chat.',
      notOnRecord: 'not on record',
      sha: 'Payload SHA-256 (recorded):',
      copy: 'Copy as JSON',
      done: 'Delivered: clear it from this page',
    },
  },
  postOps: {
    title: 'After operations',
    subtitle: 'Every operation a registry completed (registration, renewal, registrar transfer, registrant change…): what its TLD requires and where each step is.',
    howTitle: 'How it works',
    how: 'Registry modules (EPP/REST) only run the command and record a domain event. The dossier system receives the event and looks up what the TLD requires for that operation (config/compliance-requirements.yaml): the registrar signs the dossier, the dossier is synced to VNNIC HSTM (.vn), a change report goes to VNNIC international-domain reporting, or nothing.',
    version: 'Requirements in force: {{version}}',
    allStatuses: 'All statuses',
    loading: 'Loading…',
    loadError: 'The list could not be loaded',
    empty: 'No operation yet',
    nothing: 'Nothing required',
    col: { domain: 'Domain', operation: 'Operation', status: 'Status', steps: 'Steps', occurred: 'When' },
    runStatus: { IN_PROGRESS: 'In progress', ATTENTION: 'Needs attention', COMPLETED: 'Completed', NOTHING_REQUIRED: 'Nothing required' },
    stepStatus: {
      WAITING: 'Waiting for the registrar signature',
      QUEUED: 'In the signature queue',
      REQUESTED: 'Handed over',
      ACCEPTED: 'Accepted',
      IN_PROGRESS: 'Sending / awaiting result',
      DONE: 'Done',
      REJECTED: 'Rejected',
      FAILED: 'Failed',
      NOT_SUPPORTED: 'Not supported',
      DISABLED: 'Switched off',
      SUPERSEDED: 'Dossier replaced: its replacement is signed',
    },
    action: { REGISTRAR_SIGNATURE: 'Registrar signs the dossier', DOSSIER_SYNC: 'Dossier sync', CHANGE_REPORT: 'Change report' },
    backfill: {
      button: 'Backfill',
      title: 'Backfill .vn dossiers completed before the post-operation steps',
      body: '.vn dossiers completed before ADR-024 have no post-operation run, so they never reached the registrar-signature queue or the HSTM sync. The backfill starts the run of each domain\'s latest operation (registration, transfer-in, registrant change…). Preview the counts first; running it again creates nothing twice.',
      since: 'Dossiers completed since',
      sinceHint: 'Vietnam time; only active .vn domains with a complete dossier.',
      preview: 'Preview',
      run: 'Backfill {{count}} dossiers',
      found: '{{count}} dossiers to backfill: {{events}} from their operation event, {{dossiers}} from the dossier (domains imported from the former system).',
      done: '{{created}} runs created; {{already}} already existed. The dossiers are in the registrar-signature queue.',
      more: 'More dossiers remain after this pass: run the backfill again.',
      source: { EVENT: 'from the event', DOSSIER: 'from the dossier' },
    },
    operation: {
      REGISTER: 'Registration',
      RENEW: 'Renewal',
      TRANSFER_IN: 'Transfer in',
      TRANSFER_OUT: 'Transfer out',
      CHANGE_REGISTRANT: 'Registrant change',
      NAME_CHANGE: 'Name change',
      UPDATE_CONTACTS: 'Contact update',
      RESTORE: 'Restore',
      DELETE: 'Deletion',
    },
  },
  tracking: {
    title: 'Daily domain tracking report',
    subtitle: 'Every day VNNIC receives the previous day\'s .vn requests and issued names, per channel: available names, free biz.vn, free id.vn, suggested names.',
    howTitle: 'How it works',
    how: 'At 01:00 (Vietnam time) the previous day\'s report is opened, counted and sent through the VNNIC Registrar API (configured in Settings › Connections). When VNNIC already holds the day it is updated (PUT), otherwise sent (POST) — as the HostBill module in production does. Failures are retried automatically; "Resend" counts again and sends now.',
    rules: 'Request = a .vn registration order placed that day; issued = a .vn name the registry created that day. Free biz.vn / id.vn = with a VNNIC promotion code. Suggested names: the platform has no suggestion channel, always 0.',
    notConfiguredTitle: 'VNNIC Registrar API not configured',
    notConfigured: 'Reports wait until the VNNIC Registrar API is enabled in Settings › Connections.',
    otherDay: 'Send the report of another day',
    previewAndSend: 'Review the counts and send',
    daysTitle: 'Reported days',
    loading: 'Loading…',
    loadError: 'Could not load the reports',
    previewError: 'Could not count the day',
    empty: 'No report yet',
    nextAttempt: 'Next attempt',
    created: 'Sent (POST)',
    corrected: 'Updated (PUT)',
    resend: 'Resend',
    resendTitle: 'Send the report of {{date}}',
    resendBody: 'The counts of {{date}} are computed again as below and sent to VNNIC now; if VNNIC already holds the day it is updated.',
    sent: 'Report of {{date}} sent ({{method}}).',
    col: { date: 'Day reported', status: 'Status', counts: 'Counts', sent: 'Sent', attempts: 'Attempts', lastError: 'Last error' },
    notSent: 'Not sent: the VNNIC Registrar API is not configured or its configuration is not usable (Settings › Connections › VNNIC).',
    status: { PENDING: 'Waiting', SENDING: 'Sending', SENT: 'Sent', RETRY_WAIT: 'Failed — retrying', FAILED: 'Failed' },
    attemptsCount_one: '{{count}} attempt',
    attemptsCount_other: '{{count}} attempts',
    requests: 'Requests',
    issued: 'Issued',
    channel: { available: 'Available', bizFree: 'Free biz.vn', idFree: 'Free id.vn', suggested: 'Suggested' },
    field: {
      availableDomainRequest: 'Available — requests',
      availableDomainIssued: 'Available — issued',
      bizFreeRequest: 'Free biz.vn — requests',
      bizFreeIssued: 'Free biz.vn — issued',
      idFreeRequest: 'Free id.vn — requests',
      idFreeIssued: 'Free id.vn — issued',
      suggestedDomainRequest: 'Suggested — requests',
      suggestedDomainIssued: 'Suggested — issued',
    },
    // Why a day was not sent, by kind of failure; VNNIC's own answer is the technical detail.
    error: {
      invalid: 'VNNIC refused the report: data or format not accepted.',
      unauthenticated: 'VNNIC refused the Registrar API credentials (client id / secret, or the source IP is not allowed).',
      forbidden: 'VNNIC refused access to the Registrar API (credentials or source IP).',
      notFound: 'VNNIC does not know the report address.',
      conflict: 'VNNIC refused the report: it conflicts with the day it holds.',
      rateLimited: 'VNNIC asked to slow down.',
      server: 'VNNIC failed or answered something unreadable.',
      network: 'VNNIC could not be reached.',
      other: 'The report could not be sent to VNNIC.',
    },
  },
  signature: {
    title: 'Awaiting registrar signature',
    subtitle: 'Dossiers waiting for the registrar\'s digital signature. Every day at {{at}} they are collected into one batch for the signing service (SmartCA: up to 100 documents per confirmation).',
    signNow: 'Sign now',
    signNowDone: '{{count}} dossiers collected into a signing batch; the signing service will ask for confirmation.',
    notice: {
      notConfigured: 'SmartCA is not configured: files wait in the queue, unsigned',
      off: 'Signing is switched off: files wait in the queue, unsigned',
      mock: 'The test signer is in use (development machines only): files are not really signed',
      open: 'open Settings › Connections › SmartCA',
    },
    // Why a signing request was refused (admin-api codes; the server's text is never shown).
    error: {
      PRECHECK_FAILED: 'Not signed — the dossier did not pass the checks before signing:',
      SIGNING_OFF: 'Signing is switched off',
      SMARTCA_NOT_CONFIGURED: 'SmartCA is not configured',
      SIGNING_NOT_READY: 'No dossier is ready to sign: {{detail}}',
      pendingCheck: '{{count}} being checked',
      needsStaff: '{{count}} waiting for staff',
      NOTHING_TO_SIGN: 'Nothing is waiting to be signed',
      INVALID: 'This file cannot be sent for signing: {{message}}',
      OTHER: 'The signing request could not be sent. Reload the page and try again.',
      details: 'Details',
      meta: 'Code {{code}} · request {{requestId}}',
    },
    queueTitle: 'Signature queue',
    queueEmpty: 'No dossier is waiting for a signature',
    batchesTitle: 'Signing batches',
    batchesEmpty: 'No batch yet',
    loading: 'Loading…',
    loadError: 'Could not be loaded',
    transaction: 'Transaction {{n}} ({{count}} documents)',
    expires: 'expires',
    counts: '{{signed}} signed · {{failed}} failed / {{total}}',
    col: {
      domain: 'Domain',
      status: 'Status',
      attempts: 'Attempts',
      mismatches: 'File mismatches',
      queued: 'Queued',
      lastError: 'Last error',
      precheck: 'Checks before signing',
      date: 'Date',
      counts: 'Result',
      signer: 'Signing service',
      detail: 'Detail',
    },
    trigger: { SCHEDULED: 'scheduled', MANUAL: 'sign now' },
    openPrecheck: 'Open the work item',
    precheckPassed: 'Dossier checked',
    queueStatus: { PENDING_CHECK: 'Checking the dossier', REVIEW: 'Staff must fix the dossier', QUEUED: 'Waiting', IN_BATCH: 'In a batch', SIGNED: 'Signed', CANCELLED: 'Cancelled' },
    itemStatus: { PENDING: 'Awaiting confirmation', SIGNED: 'Signed', MISMATCH: 'File mismatch', FAILED: 'Failed', EXPIRED: 'Expired', REJECTED: 'Rejected' },
    batchStatus: {
      EMPTY: 'Nothing to sign',
      QUEUED: 'Waiting to be sent',
      SENT: 'Sending',
      PREPARING: 'Preparing',
      WAITING_APPROVAL: 'Awaiting confirmation in the app',
      SIGNED: 'Signed',
      PARTIALLY_FAILED: 'Partly signed',
      EXPIRED: 'Confirmation expired',
      REJECTED: 'Rejected',
      FAILED: 'Failed',
    },
    signer: { MOCK: 'Mock (testing)', SMARTCA: 'VNPT SmartCA', OFF: 'Off' },
    settings: {
      title: 'Batch settings',
      dailyAt: 'Daily collection time (Vietnam time)',
      dailyAtHint: 'HH:MM, 16:00 by default.',
      chunk: 'Documents per confirmation',
      chunkHint: 'SmartCA allows up to 100.',
      window: 'Confirmation window (seconds)',
      signer: 'Signing service',
      save: 'Save settings',
      saved: 'Settings saved.',
    },
  },
  vnnic: {
    unofficial: {
      title: 'Unofficial draft schemas',
      body: 'No official VNNIC template or submission API is on file. Every 0805A / 0805B batch is built from an internal UNOFFICIAL_DRAFT schema and must be checked against VNNIC’s current requirements before it is sent. Do not present these files as an official VNNIC template.',
      badge: 'Unofficial draft',
      badgeVersion: 'Unofficial draft · {{version}}',
      badgeHint: 'Internal draft. No official VNNIC template or API is on file.',
      official: 'Official schema',
      officialVersion: 'Official · {{version}}',
    },
    reportType: { '0805A': '0805A · annual', '0805B': '0805B · quarterly' },
    receipts: {
      empty: 'No outcome has been recorded yet.',
      col: {
        recorded: 'Recorded',
        outcome: 'Outcome',
        reference: 'VNNIC reference',
        evidence: 'Evidence',
        note: 'Note',
      },
    },
    payload: {
      withheld: '{{label}}: withheld. Seeing the submitted data needs the state-reporting management permission (a TOTP-verified session).',
      none: '{{label}}: none.',
    },
    evidenceHelp: 'Object-store key of the stored proof (VNNIC reply, portal receipt or e-mail). Upload it first; the outcome is rejected without it.',
    resolveIn: {
      sync: 'Dossier sync',
      reports: 'Reports',
      dossiers: 'Dossiers',
      registryList: 'VNNIC domain list',
    },
    registryList: {
      checkNow: 'Check now',
      checking: 'Checking…',
      failure: {
        UNAUTHORIZED: 'VNNIC refused the lookup account (wrong credentials, or the server address is not allowed by VNNIC).',
        REJECTED: 'VNNIC refused the request.',
        CONNECTION_FAILED: 'VNNIC could not be reached (network, server name or certificate).',
        INDETERMINATE: 'VNNIC did not answer in time or failed.',
        UNEXPECTED_ANSWER: 'VNNIC answered with something other than the list.',
        NOT_FOUND: 'VNNIC answered without a list.',
        OTHER: 'unknown error.',
      },
      title: 'VNNIC domain list',
      description: 'The list of names VNNIC records for Tino, compared with the .vn domains we manage through the VNNIC connection. Differences are reported for follow-up, never corrected automatically.',
      loading: 'Loading the comparison…',
      loadError: 'Could not load the VNNIC domain list comparison',
      never: 'No comparison yet',
      neverHint: 'The platform compares the lists every few hours once the VNNIC lookup account is set up (Settings › Connections › VNNIC). Press “Check now” to run it at once.',
      checkedAt: 'Compared {{rel}}',
      counts: 'VNNIC lists {{vnnic}} · we expect {{local}} · grace {{grace}}',
      missingAtVnnic: 'Missing at VNNIC',
      unknownLocally: 'Unknown to us',
      lastFailed: 'The latest read of the list failed {{rel}}: {{why}} Figures below are from the last completed comparison.',
      noMismatches: 'The lists match',
      noMismatchesHint: 'Every expected .vn name is on VNNIC’s list, and VNNIC lists nothing we do not manage.',
      kind: {
        MISSING_AT_VNNIC: 'Missing at VNNIC',
        UNKNOWN_LOCALLY: 'Unknown to us',
      },
      col: {
        domain: 'Domain',
        kind: 'Mismatch',
        localState: 'Our state',
        vnnicExpiry: 'VNNIC expiry',
      },
      notManaged: 'not managed',
    },
    coverage: {
      title: 'Coverage / Mismatch',
      subtitle: 'Which managed domains have a complete dossier, are acknowledged by VNNIC and are in the reporting set, and where they are not.',
      loading: 'Loading coverage…',
      loadError: 'Could not load VNNIC coverage',
      fullyCovered: 'Fully covered',
      ofTotal: 'of {{total}}',
      coveredMeter: 'Managed domains fully covered',
      coveredHint: 'Managed domains with a complete dossier and no open VNNIC exception.',
      openExceptions: '{{count}} open exception(s).',
      oldestSync: 'Oldest unacknowledged sync',
      waitingSince: 'Waiting since {{at}} ({{rel}}).',
      allAcknowledged: 'Every submitted .vn dossier has been acknowledged by VNNIC.',
      projected: 'International names projected',
      projectedHint: 'International TLD names held by Vietnamese registrants, projected into the 0805A / 0805B reporting set.',
      toSync: 'Dossier sync',
      toReports: 'International reports',
      vnCoverage: '.vn coverage',
      vnManaged: 'Managed .vn names',
      vnManagedCaption: 'share of all managed domains',
      dossierComplete: 'Dossier complete',
      acknowledged: 'VNNIC acknowledged',
      ofVn: 'of managed .vn names',
      queues: 'Exception queues',
      queuesHint: 'Every managed domain that is not fully covered sits in at least one queue. Select a queue to list its domains.',
      showAll: 'Show all exceptions',
      exceptionsFor: 'Exceptions · {{gap}}',
      allExceptions: 'All exceptions',
      loadingExceptions: 'Loading exceptions…',
      exceptionsError: 'Could not load exceptions',
      queueEmpty: 'This queue is empty',
      noExceptions: 'No open exceptions',
      allCovered: 'Every managed domain in scope is fully covered.',
      col: {
        domain: 'Domain',
        tldClass: 'TLD class',
        gap: 'Gap',
        resolve: 'Resolve in',
      },
      dossiersByStatus: 'Dossiers by status',
      syncByStatus: 'VNNIC sync by status',
      nothingToCount: 'Nothing to count',
      latestReports: 'Latest report states',
      allBatches: 'All batches',
      noReports: 'No report generated yet',
      schemaNote: 'Whether a batch’s schema is official or an unofficial draft is shown per batch on International Reports.',
    },
    reportCols: {
      report: 'Report',
      period: 'Period',
      revision: 'Revision',
      status: 'Status',
      records: 'Records',
      invalid: 'Invalid',
      schema: 'Schema',
      generated: 'Generated',
    },
    sync: {
      title: 'Dossier Sync',
      subtitle: 'Registrant dossiers of .vn names submitted to VNNIC: what is waiting, rejected or needs a correction.',
      loading: 'Loading sync rows…',
      loadError: 'Could not load dossier sync',
      allStatuses: 'All statuses',
      count: '{{count}} dossier(s)',
      emptyStatus: 'No dossiers in {{status}}',
      empty: 'No dossiers to sync',
      otherCode: 'Code returned by VNNIC',
      col: {
        domain: 'Domain',
        operation: 'Operation',
        status: 'Status',
        attempts: 'Attempts',
        reference: 'VNNIC reference / last error',
        lastError: 'Last error',
        nextAttempt: 'Next attempt',
        changed: 'Changed',
      },
      dossier: 'dossier',
      notInList: 'The selected dossier is not in this list',
      notInListHint: 'Its status may have changed so it no longer matches the filter.',
      showAllStatuses: 'Show all statuses',
      closeDetails: 'Close details',
      detail: {
        syncStatus: 'Sync status',
        operation: 'Operation',
        dossier: 'Dossier',
        domain: 'Domain',
        openDomain: 'Open domain',
        attempts: 'Attempts',
        reference: 'VNNIC reference',
        lastError: 'Last error',
        nextAttempt: 'Next attempt',
        statusChanged: 'Status changed',
        lastTransaction: 'Last transaction',
        nothingSubmitted: 'Nothing has been submitted to VNNIC for this dossier yet.',
        loadingTx: 'Loading transaction…',
        txError: 'Could not load the transaction',
      },
      tx: {
        transaction: 'Transaction',
        status: 'Status',
        operation: 'Operation',
        attempt: 'Attempt',
        adapter: 'Adapter',
        schemaVersion: 'Schema version',
        submitted: 'Submitted',
        completed: 'Completed',
        reference: 'VNNIC reference',
        error: 'Error',
        requestPayload: 'What we sent',
        responsePayload: 'What VNNIC returned',
        receipts: 'Receipts',
        actions: 'Actions',
        noAction: 'No manual action applies: outcomes are recorded only for Submitted or Unknown transactions, and only Rejected or Correction-required syncs can be resubmitted.',
      },
      outcome: {
        title: 'Record VNNIC outcome for attempt {{attempt}}',
        unknown: 'This transaction’s outcome is unknown: it may have reached VNNIC. Confirm the answer with VNNIC and record it here; do not resubmit blindly.',
        outcome: 'Outcome',
        reference: 'VNNIC reference (required)',
        code: 'VNNIC error code (required)',
        message: 'VNNIC message (required)',
        evidence: 'Evidence object key (required)',
        note: 'Note (optional)',
        submit: 'Record outcome',
        busy: 'Recording…',
        done: 'Outcome recorded.',
      },
      resubmit: {
        title: 'Resubmit {{domain}} to VNNIC',
        body: 'Queues the corrected dossier for a new submission (status becomes Resubmit pending). Make sure the correction VNNIC asked for is in the dossier first.',
        bodyVersion: 'Queues dossier version {{version}} for a new submission (status becomes Resubmit pending). Make sure the correction VNNIC asked for is in the dossier first.',
        submit: 'Resubmit',
        open: 'Resubmit…',
        busy: 'Resubmitting…',
        done: 'Resubmission queued.',
        doneDomain: '{{domain}} queued for resubmission.',
      },
    },
    reports: {
      title: 'International Reports',
      subtitle: '0805A (annual) and 0805B (quarterly) reports to VNNIC of international TLD names held by Vietnamese registrants.',
      generate: {
        title: 'Generate a report batch',
        hint: 'Builds a new revision from the reporting projection and validates it against the current draft schema. A period whose previous revision is still pending must be finished (acknowledged or rejected) first.',
        report: 'Report',
        year: 'Year',
        quarter: 'Quarter',
        submit: 'Generate batch',
        busy: 'Generating…',
        done: 'Generated {{type}} {{period}} revision {{revision}}: {{status}}, {{records}} record(s), {{invalid}} invalid.',
      },
      filter: 'Report',
      allReports: 'All reports',
      count: '{{count}} batch(es)',
      loading: 'Loading batches…',
      loadError: 'Could not load report batches',
      empty: 'No report batches yet',
      emptyHint: 'Generate the first batch above.',
      supersedes: 'supersedes an earlier revision',
      batch: {
        title: '{{type}} · {{period}} · revision {{revision}}',
        loading: 'Loading batch…',
        loadError: 'Could not load the batch',
        refreshError: 'Could not refresh the batch',
        close: 'Close batch',
        unofficial: 'Built from an unofficial draft schema ({{version}}). This file is not an official VNNIC template.',
        id: 'Batch',
        period: 'Period (UTC)',
        records: 'Records',
        recordsValue: '{{records}} ({{invalid}} invalid)',
        checksum: 'Checksum',
        verified: 'Verified',
        mismatch: 'Mismatch',
        mismatchHint: 'The stored checksum no longer matches the batch items. Do not submit this file.',
        schema: 'Schema',
        generated: 'Generated',
        generatedBy: '{{at}} by {{who}}',
        exported: 'Exported',
        exportObject: 'Export object',
        submitted: 'Submitted',
        acknowledged: 'Acknowledged',
        reference: 'VNNIC reference',
        supersedes: 'Supersedes',
        supersededBy: 'Superseded by',
        revision: 'Revision {{revision}}',
        firstRevision: 'Nothing (first revision)',
        chain: 'Revision chain',
        chainLabel: 'Revisions of this period',
        tabs: {
          summary: 'Summary',
          items: 'Items',
          actions: 'Actions',
          receipts: 'Receipts',
        },
        items: 'Items',
        invalidItems: '{{count}} invalid',
        noItems: 'This batch has no items.',
        col: {
          line: 'Line',
          domain: 'Domain',
          change: 'Change',
          validation: 'Validation',
          submission: 'Submission',
          payload: 'Reported data',
        },
        valid: 'Valid',
        invalid: 'Invalid',
        line: 'Line {{line}}',
      },
      actions: {
        none: 'No action applies to a batch in status “{{status}}”. Export needs a validated batch; outcomes follow export (submitted) and submission (acknowledged or rejected). Generate a new revision to correct it.',
        export: 'Export file',
        exporting: 'Exporting…',
        exported: 'Exported through the {{adapter}} adapter; batch is now {{status}}.',
        record: 'Record outcome',
      },
      outcome: {
        title: 'Record VNNIC outcome',
        outcome: 'Outcome',
        referenceRequired: 'VNNIC reference (required)',
        referenceOptional: 'VNNIC reference (optional)',
        code: 'VNNIC error code (required)',
        message: 'VNNIC message (required)',
        evidence: 'Evidence object key (required)',
        note: 'Note (optional)',
        itemErrors: 'Item errors (optional)',
        itemErrorsHint: 'Lines VNNIC named in its rejection. Each needs a line number from this batch, a code and a message.',
        line: 'Line',
        code_: 'Code',
        message_: 'Message',
        removeItem: 'Remove item error {{n}}',
        addItem: 'Add item error',
        submit: 'Record outcome',
        busy: 'Recording…',
        done: 'Recorded outcome “{{outcome}}”; batch is now {{status}}.',
        label: {
          SUBMITTED: 'Submitted to VNNIC',
          ACKNOWLEDGED: 'Acknowledged by VNNIC',
          REJECTED: 'Rejected by VNNIC',
        },
      },
    },
    corrections: {
      title: 'Corrections',
      subtitle: 'Dossier syncs VNNIC rejected or asked to correct, and report batches that were rejected or revised.',
      loadError: 'Could not load corrections',
      sync: 'Dossier sync',
      allSyncs: 'All syncs',
      nothing: 'Nothing awaits correction',
      col: {
        domain: 'Domain',
        sync: 'Sync',
        dossier: 'Dossier',
        answer: 'VNNIC answer',
        attempts: 'Attempts',
        since: 'Since',
        action: 'Action',
      },
      attemptVia: 'attempt {{attempt}} via {{adapter}}',
      nextAttempt: 'next attempt {{when}}',
      reports: 'Report revisions',
      reportsHint: 'Reports are immutable; a correction is a new revision superseding the previous one.',
      noReports: 'No rejected or revised reports',
      rcol: {
        report: 'Report',
        period: 'Period',
        revision: 'Revision',
        status: 'Status',
        records: 'Records',
        invalid: 'Invalid / rejected',
        chain: 'Chain',
      },
      schema: 'schema {{version}}',
      supersedes: 'supersedes',
      supersededBy: 'superseded by',
      needsRevision: 'Needs a new revision',
      stats: {
        rejected: 'Rejected syncs',
        correction: 'Correction required',
        reports: 'Reports needing a revision',
      },
    },
  },
  rde: {
    title: 'RDE deposits',
    subtitle: 'Registrar data escrow: scheduled full and incremental deposits of registration data to the escrow agent.',
    draft: {
      title: 'Unofficial draft format',
      body: 'The official ICANN Registrar Data Escrow specification (file layout, field mapping, encryption and transfer parameters) is not on file. Deposits are produced in the internal UNOFFICIAL_DRAFT format and are not ICANN RDE deposits.',
      badge: 'Unofficial draft',
      badgeFormat: 'Unofficial draft · {{format}}',
      badgeHint: 'Internal UNOFFICIAL_DRAFT format: the official Registrar Data Escrow specification is not on file',
      official: 'Official',
    },
    noPolicy: 'No ICANN_REGISTRAR_DATA_ESCROW policy is in force: the escrow pipeline fails closed and plans no deposits.',
    noPolicyTitle: 'No escrow policy in force',
    policy: {
      title: 'Escrow policy in force',
      policy: 'Policy',
      effective: 'Effective',
      format: 'Format',
      scope: 'Scope',
      full: 'Full deposits',
      incremental: 'Incrementals',
      delivery: 'Delivery',
      notesTitle: 'Policy notes',
      originalHint: 'As written in the policy: {{text}}',
      param: {
        every: 'every {{value}}',
        weekday: 'on {{value}}',
        overlap: 'overlap {{value}}',
        max_attempts: 'at most {{value}} attempts',
        retry_after: 'retry after {{value}}',
        settle_after: 'settle after {{value}}',
      },
      weekdays: { MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday' },
      units: { D: '{{count}} d', H: '{{count}} h', M: '{{count}} min', S: '{{count}} s', W: '{{count}} wk' },
    },
    notes: {
      draftFormat:
        'The deposit format UNOFFICIAL_DRAFT_CSV_V1 is an internal UNOFFICIAL DRAFT pending the official Registrar Data Escrow specification text; it is NOT the ICANN RDE format. A new policy version selects the official format once it is implemented.',
      schedule:
        'Schedule follows the standard Registrar Data Escrow Specification (at least one full deposit weekly, here Sunday, plus daily incrementals). Deposits held by the registrar are kept for at least 20 years (statutory minimum, REGISTRAR_DATA_RETENTION).',
      utc: 'Slots are computed in UTC.',
    },
    filter: { status: 'Status', type: 'Type' },
    loadError: 'Could not load deposits',
    empty: 'No deposits match',
    col: {
      watermark: 'Watermark',
      type: 'Type',
      status: 'Status',
      records: 'Records',
      content: 'Content',
      delivered: 'Delivered',
      checks: 'Checks',
      delivery: 'Delivery',
      format: 'Format',
    },
    since: 'since {{at}}',
    deleted: '{{count}} deleted',
    files: '{{size}} · {{count}} files',
    checksFailed: '{{count}} failed',
    checksPassed: '{{count}} passed',
    attempts: '{{count}} attempt(s)',
    next: 'next {{when}}',
    newer: 'Newer',
    older: 'Older',
    detail: {
      title: 'RDE deposit',
      heading: 'Deposit: {{type}}',
      loadError: 'Could not load the deposit',
      unknown: 'The last delivery’s outcome is unknown: the deposit may have reached the escrow agent. It is settled only by querying the agent, never by a blind resend.',
      unknownTitle: 'Delivery outcome unknown',
      facts: 'Deposit details',
      deposit: 'Deposit',
      watermark: 'Watermark',
      since: 'Since',
      base: 'Base deposit',
      openBase: 'Open base',
      scope: 'Scope',
      policy: 'Policy',
      records: 'Records',
      deletions: 'Deletions',
      checksum: 'Checksum',
      encryption: 'Encryption',
      agent: 'Agent',
      agentRef: 'Agent reference',
      attempts: 'Delivery attempts',
      nextAttempt: 'Next attempt',
      lastError: 'Last error',
      created: 'Created',
      createdBy: '{{at}} by {{who}}',
      generated: 'Generated',
      validated: 'Validated',
      encrypted: 'Encrypted',
      delivered: 'Delivered',
      confirmed: 'Confirmed',
      files: 'Files',
      filesHint: 'Metadata of objects written to the object store',
      noFiles: 'No files generated yet',
      fcol: {
        role: 'Role',
        name: 'Name',
        size: 'Size',
        sha: 'SHA-256',
        encryption: 'Encryption',
      },
      plain: 'plain',
      bytes: '{{count}} bytes',
      checks: 'Validation checks',
      notValidated: 'Not validated yet',
      vcol: {
        check: 'Check',
        outcome: 'Outcome',
        file: 'File',
        validator: 'Validator',
        detail: 'Detail',
      },
      attemptsTitle: 'Delivery attempts',
      notDelivered: 'Not delivered yet',
      acol: { n: '#', status: 'Status', sent: 'Sent', result: 'Result' },
      receipts: 'Receipts',
      noReceipts: 'No receipt or verification notice yet',
      rcol: {
        recorded: 'Recorded',
        verification: 'Verification',
        source: 'Source',
        evidence: 'Evidence',
      },
      tabs: {
        summary: 'Summary',
        files: 'Files & checks',
        delivery: 'Delivery & receipts',
      },
    },
  },
  rdap: {
    title: 'RDAP',
    subtitle: 'RDAP and WHOIS publish only the registration-data projection, filtered by the versioned publication policy.',
    preview: {
      title: 'Lookup preview',
      hint: 'Shows how the RDAP service would publish each field for a name right now. Values of registrant fields are never displayed here.',
      domain: 'Domain',
      submit: 'Preview',
      loading: 'Looking up…',
      error: 'Lookup failed',
      notFound: '{{name}}: not found',
      notFoundHint: 'RDAP answers 404: the name is not a servable registration here, or RDAP is disabled for its TLD.',
      noPolicy: 'No policy — name only',
      noPolicyHint: 'No publication policy in force: the record fails closed to its name only',
      projected: 'projected {{when}}',
      redaction: 'redaction text “{{text}}”',
      col: { field: 'Field', shown: 'Shown as', value: 'Public value' },
      personal: 'published (personal data not echoed here)',
      yes: 'yes (registrar profile)',
      no: 'no',
      emptyList: '(none)',
    },
    loadError: 'Could not load the RDAP overview',
    on: 'On',
    off: 'Off',
    tlds: 'TLDs',
    tldsHint: 'Service switches from the TLD configuration; counts from the projection',
    noTlds: 'No TLDs configured',
    tcol: {
      tld: 'TLD',
      class: 'Class',
      rdap: 'RDAP',
      whois: 'WHOIS',
      projected: 'Projected',
      servable: 'Servable',
      pending: 'Pending refresh',
      last: 'Last projected',
    },
    policies: 'Publication policies',
    policiesHint: 'A field a policy does not list is omitted; with no policy a record fails closed to its name',
    noPolicies: 'No publication policy loaded',
    noPoliciesHint: 'Every record is served with its name only.',
    field: 'Field',
    allDomains: 'all domains',
    inForce: 'In force',
    notInForce: 'Not in force',
    redactionText: 'Redaction text',
  },
  inbox: {
    title: 'Case inbox',
    subtitle: 'Abuse, dispute and disclosure cases in one queue, overdue first. No requester or reporter contact data is shown here.',
    forbidden: 'Not shown (no permission): {{kinds}}.',
    kind: 'Case kind',
    includeClosed: 'Include closed',
    loadError: 'Could not load cases',
    empty: 'Inbox is empty',
    col: {
      kind: 'Kind',
      case: 'Case',
      domain: 'Domain',
      type: 'Type',
      status: 'Status',
      due: 'Due',
      opened: 'Opened',
    },
    stats: {
      total: 'Cases in view',
      overdue: 'Overdue',
      dueSoon: 'Due within 24 h',
    },
  },
  locks: {
    title: 'Legal locks',
    subtitle: 'Court, UDRP, registry and compliance locks that block changes to a domain. Releasing one needs four-eyes approval; dispute locks are released from their dispute.',
    noEffects: 'No REGISTRAR_LOCK_EFFECTS policy is in force: the compliance gate fails closed and every active lock blocks every operation.',
    noEffectsTitle: 'No lock-effects policy',
    state: 'Lock state',
    type: 'Type',
    allLegal: 'All legal locks',
    loadError: 'Could not load locks',
    empty: 'No locks match',
    col: {
      domain: 'Domain',
      lock: 'Lock',
      reason: 'Reason',
      placedFor: 'Placed for',
      applied: 'Applied',
      released: 'Released',
    },
    active: 'Active',
    released: 'Released',
    noCase: 'No linked case',
    domainAction: 'domain action {{action}}',
    approval: 'approval',
    effects: 'What each lock blocks',
    effectsHint: 'From {{policy}}, evaluated by the compliance gate.',
    ecol: { lock: 'Lock', blocked: 'Blocked operations' },
    nothing: 'none',
    unlockHint: 'Release a dispute lock from its dispute page (four-eyes approval with the permission “Release a legal lock”).',
  },
  policies: {
    title: 'Compliance policies',
    subtitle: 'The rules the platform applies to registrations, publication, locks and reporting: which version is in force, since when, and which team owns it.',
    loadError: 'Could not load the policies',
    empty: 'No policy matches these filters',
    emptyHint: 'Change or reset the filters to see more policies.',
    stats: { total: 'Policies', inForce: 'In force now', drafts: 'Still a draft', authorities: 'Authorities' },
    filter: { kind: 'Area', status: 'Status', search: 'Policy' },
    searchHint: 'part of the name',
    forTld: 'Policies that apply to {{zone}}',
    forTldSubtitle: 'Only the policies that apply to this TLD: by suffix, TLD class, registry, ICANN scope or every domain. Registrar-wide rules are not listed here.',
    emptyTld: 'No policy applies to this TLD specifically',
    because: {
      SUFFIX: 'The {{zone}} suffix',
      TLD_CLASS: 'The class of {{zone}}',
      REGISTRY: 'The registry of {{zone}}',
      ICANN_SCOPE: 'Every ICANN-governed TLD',
      ALL_DOMAINS: 'Every domain',
    },
    col: {
      appliesBecause: 'Applies because',
      policy: 'Policy',
      kind: 'Area',
      authority: 'Set by',
      owner: 'Owner',
      version: 'Version',
      status: 'Status',
      effective: 'In force',
      origin: 'Kept in',
      versions: 'Versions',
    },
    inForce: 'In force',
    notInForce: 'Not in force',
    openEnded: 'no end date',
    fromUntil: '{{from}} → {{until}}',
    since: 'since {{from}}',
    draftNote: 'A rule that is still a draft is shown here but never applied on its own. Compliance has to sign it off before the platform enforces it.',
    detail: {
      title: 'Policy',
      loadError: 'Could not load this policy',
      facts: 'Version in force',
      versions: 'Published versions ({{count}})',
      versionsHint: 'A published version is never edited. A change is a new version with its own start date, so past decisions keep the rule they were made under.',
      files: 'Versions written in the repository ({{count}})',
      filesHint: 'The rule as the team wrote it. It can be ahead of what the platform publishes.',
      noVersions: 'This rule is only written in the repository; nothing is published in the platform yet.',
      noFiles: 'This rule is only published in the platform.',
      decisions: 'Recent decisions ({{count}})',
      decisionsHint: 'The last operations this rule was applied to.',
      noDecisions: 'No operation has been decided with this rule yet',
      dcol: { when: 'When', operation: 'Operation', subject: 'Applied to', outcome: 'Outcome', version: 'Version' },
      content: 'Rule content',
      notes: 'Notes',
      parameters: 'Values',
      owner: 'Owner',
      authority: 'Set by',
      approved: 'Approved',
      approvedBy: 'Approved by',
      source: 'Legal source',
      origin: 'Kept in',
      checksum: 'Content fingerprint',
      id: 'ID',
      vcol: { version: 'Version', status: 'Status', effective: 'In force', approved: 'Approved', source: 'Kept in' },
    },
  },
  deadlines: {
    title: 'Regulatory deadlines',
    subtitle: 'What the registrar owes a regulator, when it is due and whether it has been handed in. Overdue obligations come first.',
    loadError: 'Could not load the deadlines',
    empty: 'No deadline matches these filters',
    emptyHint: 'Change or reset the filters to see more deadlines.',
    stats: { open: 'Still open', overdue: 'Overdue', done: 'Handed in', next: 'Next one due' },
    filter: { status: 'Status', obligation: 'Obligation' },
    col: {
      obligation: 'Obligation',
      period: 'Period',
      due: 'Due',
      status: 'Status',
      owner: 'Owner',
      report: 'Handed in as',
      source: 'Source',
    },
    noPeriod: 'no fixed period',
    noReport: 'Nothing handed in yet',
    openReport: 'Open the report',
    revision: 'revision {{revision}}',
    sourceNote: 'Every due date here is recorded by compliance together with the notice or legal text it comes from; none of them is built into the platform.',
    overdueNote: 'Some obligations are past their due date and still open. Handle those first.',
    none: 'No deadline is recorded yet.',
  },
  lockDetail: {
    title: 'Legal lock',
    loadError: 'Could not load this lock',
    facts: 'Lock',
    domain: 'Domain',
    type: 'Lock',
    state: 'State',
    reason: 'Reason',
    caseReference: 'Case reference',
    applied: 'Applied',
    appliedBy: 'Applied by',
    released: 'Released',
    releasedBy: 'Released by',
    releaseReason: 'Reason for release',
    approval: 'Second-operator approval',
    placedFor: 'Placed for',
    blocks: 'What this lock blocks',
    blocksNone: 'Under the version in force this lock blocks nothing.',
    noEffects: 'No lock rule is in force, so this lock blocks every operation until one is published.',
    registry: 'Statuses at the registry',
    noRegistryStatuses: 'The registry reports no status for this name.',
    other: 'Other locks on this name ({{count}})',
    noOther: 'This is the only lock on the name.',
    unlockHint: 'A dispute lock is released from its dispute page, and only after a second operator approves it.',
    id: 'ID',
  },
  syncTx: {
    title: 'Dossier submission',
    heading: 'Submission to VNNIC · attempt {{attempt}}',
    loadError: 'Could not load this submission',
    facts: 'Submission',
    domain: 'Domain',
    dossier: 'Dossier',
    dossierValue: 'version {{version}}',
    syncStatus: 'Dossier as a whole',
    status: 'This attempt',
    operation: 'Operation',
    attempt: 'Attempt',
    channel: 'Sent through',
    schema: 'Form version',
    submitted: 'Sent',
    completed: 'Answered',
    reference: 'VNNIC reference',
    error: 'VNNIC answer',
    receipts: 'Recorded answers',
    payloads: 'What was sent and received',
    allSubmissions: 'All dossier submissions',
    id: 'ID',
  },
  leRequest: {
    title: 'Authority request',
    loadError: 'Could not load this request',
    facts: 'Request',
    agency: 'Authority',
    jurisdiction: 'Jurisdiction',
    reference: 'Their reference',
    legalBasis: 'Legal basis',
    received: 'Received',
    deadline: 'Answer due',
    status: 'Status',
    response: 'Our answer',
    respondedAt: 'Answered',
    recordedBy: 'Recorded by',
    case: 'Abuse case',
    domain: 'Domain',
    history: 'History',
    noHistory: 'Nothing recorded for this request yet',
    overdue: 'The answer to this authority is overdue since {{when}}.',
    privacy: 'Registrant data is never shown here. It is released only through a disclosure case, and every release is audited.',
    updateOnCase: 'Move this request along from the abuse case',
    id: 'ID',
  },
  enums: {
    abuseStatus: {
      NEW: 'New',
      TRIAGED: 'Triaged',
      INVESTIGATING: 'Investigating',
      ACTION_REQUIRED: 'Action required',
      WAITING: 'Waiting',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
    priority: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      CRITICAL: 'Critical',
    },
    abuseCategory: {
      SPAM: 'Spam',
      PHISHING: 'Phishing',
      MALWARE: 'Malware',
      BOTNET: 'Botnet',
      PHARMING: 'Pharming',
      FRAUD: 'Fraud',
      CSAM: 'CSAM',
      TRADEMARK: 'Trademark',
      OTHER: 'Other',
    },
    abuseSource: {
      REPORT: 'Report',
      LAW_ENFORCEMENT: 'Law enforcement',
      REGISTRY: 'Registry',
      ICANN: 'ICANN',
      INTERNAL: 'Internal',
    },
    channel: {
      WEB_FORM: 'Web form',
      EMAIL: 'Email',
      API: 'API',
      PHONE: 'Phone',
      POSTAL: 'Postal',
      INTERNAL: 'Internal',
    },
    reporterType: {
      ANONYMOUS: 'Anonymous',
      INDIVIDUAL: 'Individual',
      ORGANIZATION: 'Organization',
      LAW_ENFORCEMENT: 'Law enforcement',
      REGISTRY: 'Registry',
      ICANN: 'ICANN',
      INTERNAL: 'Internal',
    },
    leStatus: {
      RECEIVED: 'Received',
      ACKNOWLEDGED: 'Acknowledged',
      RESPONDED: 'Responded',
      REJECTED: 'Rejected',
      CLOSED: 'Closed',
    },
    domainActionStatus: {
      PENDING: 'Pending',
      APPLIED: 'Applied',
      FAILED: 'Failed',
      UNKNOWN: 'Unknown outcome',
    },
    domainAction: {
      SUSPEND: 'Suspend',
      UNSUSPEND: 'Unsuspend',
      LOCK: 'Lock',
      UNLOCK: 'Unlock',
      NOTE_ONLY: 'Note only',
    },
    disputeStatus: {
      OPENED: 'Opened',
      LOCKED: 'Locked',
      DECIDED: 'Decided',
      IMPLEMENTED: 'Implemented',
      WITHDRAWN: 'Withdrawn',
      CLOSED: 'Closed',
    },
    disputeType: {
      UDRP: 'UDRP',
      URS: 'URS',
      COURT_ORDER: 'Court order',
      REGISTRY_ACTION: 'Registry action',
      GOVERNMENT_REQUEST: 'Government request',
      OTHER: 'Other',
    },
    disputeDecision: {
      TRANSFER: 'Transfer',
      CANCELLATION: 'Cancellation',
      SUSPENSION: 'Suspension',
      DENIED: 'Denied',
      SETTLED: 'Settled',
      OTHER: 'Other',
    },
    closeOutcome: { WITHDRAWN: 'Withdrawn', CLOSED: 'Closed' },
    approvalStatus: {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      EXPIRED: 'Expired',
      EXECUTED: 'Executed',
      CANCELLED: 'Cancelled',
    },
    disclosureStatus: {
      RECEIVED: 'Received',
      UNDER_REVIEW: 'Under review',
      INFO_REQUESTED: 'Info requested',
      APPROVED: 'Approved',
      PARTIALLY_APPROVED: 'Partially approved',
      DENIED: 'Denied',
      RELEASED: 'Released',
      CLOSED: 'Closed',
    },
    requesterType: {
      LAW_ENFORCEMENT: 'Law enforcement',
      IP_RIGHTS_HOLDER: 'IP rights holder',
      CYBERSECURITY: 'Cybersecurity',
      CONSUMER_PROTECTION: 'Consumer protection',
      LEGAL_PROCEEDING: 'Legal proceeding',
      OTHER: 'Other',
    },
    gap: {
      MISSING_DOSSIER: 'Missing dossier',
      INCOMPLETE_DOSSIER: 'Incomplete dossier',
      VERIFICATION_NOT_VALID: 'Verification not valid',
      SUBJECT_MISMATCH: 'Subject mismatch',
      VNNIC_SYNC_MISSING: 'Sync missing',
      VNNIC_SYNC_PENDING: 'Sync pending',
      VNNIC_SYNC_REJECTED: 'Sync rejected',
      VNNIC_SYNC_ERROR: 'Sync error',
      REPORT_PROJECTION_MISSING: 'Report projection missing',
      VNNIC_REGISTRY_MISSING: 'Not on VNNIC list',
    },
    gapHint: {
      MISSING_DOSSIER: 'Managed domain with no dossier at all.',
      INCOMPLETE_DOSSIER: 'Dossier exists but is not COMPLETE.',
      VERIFICATION_NOT_VALID: 'Identity verification expired, failed or missing.',
      SUBJECT_MISMATCH: 'Dossier subject differs from the domain registrant.',
      VNNIC_SYNC_MISSING: '.vn name never submitted to VNNIC.',
      VNNIC_SYNC_PENDING: 'Submitted or queued, awaiting VNNIC acknowledgement.',
      VNNIC_SYNC_REJECTED: 'VNNIC rejected or asked for a correction.',
      VNNIC_SYNC_ERROR: 'Submission failed technically; the platform retries.',
      REPORT_PROJECTION_MISSING: 'International name not projected into the reporting set.',
      VNNIC_REGISTRY_MISSING: 'Managed .vn name that VNNIC does not list among our domains (latest domain-list comparison).',
    },
    syncStatus: {
      NOT_SYNCED: 'Not synced',
      SYNC_PENDING: 'Sync pending',
      QUEUED: 'Queued',
      SUBMITTING: 'Submitting',
      SUBMITTED: 'Submitted',
      ACKNOWLEDGED: 'Acknowledged',
      REJECTED: 'Rejected',
      CORRECTION_REQUIRED: 'Correction required',
      RESUBMIT_PENDING: 'Resubmit pending',
      SYNC_ERROR: 'Sync error',
      SUPERSEDED: 'Superseded',
    },
    /** Codes the platform records on a dossier sync (the regulatory engine's gateway outcomes, modules/regulatory/core/src/engine). */
    syncErrorCode: {
      UNKNOWN: 'Not known whether VNNIC received the dossier',
      NOT_RECEIVED: 'VNNIC has no record of the submission',
      MOCK_UNAVAILABLE: 'The simulated VNNIC gateway did not answer (test)',
      EXPORT_FAILED: 'The export file for manual submission could not be written',
      SERIALIZATION: 'The dossier could not be prepared for sending',
      CONFIGURATION_ERROR: 'The VNNIC connection is not set up correctly; nothing was sent',
      HSTM_UNREACHABLE: 'VNNIC’s dossier system could not be reached',
      HSTM_UNAUTHORIZED: 'VNNIC refused the credentials or the IP address',
      HSTM_REQUEST_REJECTED: 'VNNIC refused the request',
      HSTM_REVIEW_REJECTED: 'VNNIC reviewed the dossier and asks for corrections',
      HSTM_PAYLOAD_INVALID: 'The dossier package is not in VNNIC’s format',
      HSTM_PAYLOAD_INCOMPLETE: 'The dossier lacks required information or files',
      HSTM_FILE_UNAVAILABLE: 'A file of the dossier could not be read',
      HSTM_FILE_FORMAT: 'A file of the dossier has the wrong format or size',
      HSTM_VALIDATION: 'VNNIC reports the dossier as invalid',
      HSTM_DOCUMENT_NOT_FOUND: 'The dossier no longer exists at VNNIC',
    },
    txStatus: {
      SUBMITTING: 'Submitting',
      SUBMITTED: 'Submitted',
      ACKNOWLEDGED: 'Acknowledged',
      REJECTED: 'Rejected',
      CORRECTION_REQUIRED: 'Correction required',
      FAILED: 'Failed',
      UNKNOWN: 'Unknown outcome',
    },
    reportStatus: {
      GENERATED: 'Generated',
      VALIDATION_FAILED: 'Validation failed',
      VALIDATED: 'Validated',
      EXPORTED: 'Exported',
      SUBMITTED: 'Submitted',
      ACKNOWLEDGED: 'Acknowledged',
      REJECTED: 'Rejected',
      SUPERSEDED: 'Superseded',
    },
    syncOperation: { SUBMIT: 'Submit', UPDATE: 'Update' },
    dossierStatus: {
      NOT_CREATED: 'Not created',
      COLLECTING: 'Collecting',
      PENDING_IDENTITY: 'Pending identity',
      PENDING_DOCUMENT: 'Pending document',
      PENDING_SIGNATURE: 'Pending signature',
      PENDING_REVIEW: 'Pending review',
      COMPLETE: 'Complete',
      INVALID: 'Invalid',
      EXPIRED: 'Expired',
      CORRECTION_REQUIRED: 'Correction required',
      SUPERSEDED: 'Superseded',
    },
    depositStatus: {
      PENDING: 'Pending',
      GENERATED: 'Generated',
      VALIDATED: 'Validated',
      VALIDATION_FAILED: 'Validation failed',
      ENCRYPTED: 'Encrypted',
      DELIVERING: 'Delivering',
      DELIVERED: 'Delivered',
      DELIVERY_FAILED: 'Delivery failed',
      DELIVERY_UNKNOWN: 'Delivery unknown',
      CONFIRMED: 'Confirmed',
      VERIFICATION_FAILED: 'Verification failed',
      ABANDONED: 'Abandoned',
    },
    depositType: { FULL: 'Full', INCREMENTAL: 'Incremental' },
    verificationStatus: {
      RECEIVED: 'Received',
      VERIFIED: 'Verified',
      VERIFICATION_FAILED: 'Verification failed',
    },
    attemptStatus: {
      SENDING: 'Sending',
      DELIVERED: 'Delivered',
      FAILED: 'Failed',
      UNKNOWN: 'Unknown outcome',
    },
    fileRole: { CONTENT: 'Content', DELIVERY: 'Delivery' },
    receiptSource: { AGENT: 'Escrow agent', OPERATOR: 'Operator' },
    checkOutcome: { PASS: 'Pass', FAIL: 'Fail' },
    escrowScope: {
      ICANN_GOVERNED_TLDS: 'ICANN-governed TLDs',
      ALL_GTLD: 'All gTLD registrations',
      GTLD: 'gTLD registrations',
      ICANN_GOVERNED: 'ICANN-governed TLDs',
      ALL: 'All registrations',
    },
    lockType: {
      REGISTRAR_LOCK: 'Registrar lock',
      TRANSFER_LOCK: 'Transfer lock',
      UDRP_LOCK: 'UDRP lock',
      COURT_LOCK: 'Court lock',
      REGISTRY_LOCK: 'Registry lock',
      COMPLIANCE_LOCK: 'Compliance lock',
    },
    lockState: { active: 'Active', released: 'Released', all: 'All' },
    caseKind: { ABUSE: 'Abuse', DISPUTE: 'Dispute', DISCLOSURE: 'Disclosure' },
    disposition: {
      PUBLISH: 'Publish',
      PUBLISH_IF_CONSENTED: 'Publish if consented',
      REDACT: 'Redact',
      CONTACT_FORM: 'Contact form',
      OMIT: 'Omit',
    },
    shown: {
      PUBLIC: 'Published',
      REDACTED: 'Redacted',
      CONTACT_FORM: 'Contact form',
      OMITTED: 'Omitted',
    },
    actorType: {
      USER: 'user',
      OPERATOR: 'operator',
      ADMIN: 'admin',
      SYSTEM: 'system',
      WORKER: 'worker',
      SERVICE: 'service',
      CUSTOMER: 'customer',
      API: 'API',
    },
    lifecycle: {
      DRAFT: 'Draft',
      AWAITING_DOSSIER: 'Awaiting dossier',
      AWAITING_PAYMENT: 'Awaiting payment',
      PENDING_CREATE: 'Pending create',
      CREATE_FAILED: 'Create failed',
      ACTIVE: 'Active',
      PENDING_RENEW: 'Pending renew',
      PENDING_TRANSFER: 'Pending transfer',
      EXPIRED: 'Expired',
      GRACE_PERIOD: 'Grace period',
      REDEMPTION: 'Redemption',
      PENDING_DELETE: 'Pending delete',
      DELETED: 'Deleted',
      TRANSFERRED_OUT: 'Transferred out',
    },
    operation: {
      CREATE: 'Create',
      REGISTER: 'Register',
      RENEW: 'Renew',
      TRANSFER: 'Transfer',
      TRANSFER_OUT: 'Transfer out',
      UPDATE: 'Update',
      UPDATE_CONTACTS: 'Update contacts',
      UPDATE_NAMESERVERS: 'Update nameservers',
      CHANGE_NAMESERVERS: 'Change nameservers',
      UPDATE_REGISTRANT: 'Change registrant',
      CHANGE_REGISTRANT: 'Change registrant',
      DELETE: 'Delete',
      RESTORE: 'Restore',
      AUTH_INFO: 'Auth info',
    },
    caseAction: {
      OPENED: 'Case opened',
      REPORT_ATTACHED: 'Report attached',
      STATUS_CHANGED: 'Status changed',
      ASSIGNED: 'Assigned',
      EVIDENCE_ADDED: 'Evidence added',
      LAW_ENFORCEMENT_REQUEST_RECORDED: 'Authority request recorded',
      LAW_ENFORCEMENT_REQUEST_UPDATED: 'Authority request updated',
      DOMAIN_ACTION_REQUESTED: 'Domain action requested',
      DOMAIN_ACTION_SETTLED: 'Domain action settled',
      SUSPEND: 'Suspend',
      UNSUSPEND: 'Unsuspend',
      LOCK: 'Lock',
      UNLOCK: 'Unlock',
      NOTE_ONLY: 'Note only',
    },
    policyKind: {
      PROTOCOL: 'Protocol',
      ICANN: 'ICANN',
      REGISTRY: 'Registry',
      TLD: 'TLD',
      COUNTRY: 'Country',
      VIETNAM: 'Vietnam',
      REGISTRAR: 'Registrar',
      DOCUMENT_REQUIREMENT: 'Required documents',
      ELIGIBILITY: 'Eligibility',
      LIFECYCLE: 'Lifecycle',
      PUBLICATION: 'Publication',
      PRICING: 'Pricing',
      AUCTION: 'Auction',
      RETENTION: 'Retention',
    },
    policyStatus: {
      DRAFT: 'Draft',
      APPROVED: 'Approved',
      ACTIVE: 'Active',
      RETIRED: 'Retired',
      REJECTED: 'Rejected',
    },
    policyOrigin: {
      DATABASE: 'Platform',
      FILE: 'Repository',
      BOTH: 'Both',
    },
    deadlineStatus: {
      open: 'Open',
      done: 'Handed in',
      waived: 'Waived',
    },
    decisionOutcome: {
      ALLOW: 'Allowed',
      ALLOW_WITH_REQUIRED_ACTIONS: 'Allowed with conditions',
      BLOCK: 'Blocked',
    },
  },
} as const;
